/**
 * Verify app route config.
 *
 * Each entry maps a URL path segment to a lazy-loaded tab component.
 * The TabRoute wrapper reads siteId from SiteContext and passes it as
 * the websiteId prop (bridging the old prop-based API until tabs are
 * migrated to useSite()).
 */

import React, { lazy } from 'react';
import { Route, Navigate } from 'react-router-dom';
import { useSite } from './components/SiteContext';

const ChatTab = lazy(() => import('./components/ChatTab'));
const VerifyTab = lazy(() => import('./components/VerifyTab').then(m => ({ default: m.VerifyTab })));
const SearchTab = lazy(() => import('./components/SearchTab'));
const ClaimsTab = lazy(() => import('./components/ClaimsTab').then(m => ({ default: m.ClaimsTab })));
const LinksView = lazy(() => import('./components/LinksView').then(m => ({ default: m.LinksView })));
const GscView = lazy(() => import('./components/GscView').then(m => ({ default: m.GscView })));
const SearchConsoleTab = lazy(() => import('./components/SearchConsoleTab').then(m => ({ default: m.SearchConsoleTab })));
const SearchConsolePage = lazy(() => import('./components/gsc-explorer/SearchConsolePage'));
const GscReport = lazy(() => import('./components/gsc-explorer/GscReport'));
const IndexView = lazy(() => import('./components/IndexView'));
const CrawlTab = lazy(() => import('./components/CrawlTab'));
const CitationAnalysisTab = lazy(() => import('./components/CitationAnalysisTab'));
const CitationOptimizerTab = lazy(() => import('./components/CitationOptimizerTab'));
const PageDiagnosticTab = lazy(() => import('./components/PageDiagnosticTab'));
const DiscoveryTab = lazy(() => import('./components/discovery').then(m => ({ default: m.DiscoveryTab })));
const ChunkAnalyzerTab = lazy(() => import('./components/chunking').then(m => ({ default: m.ChunkAnalyzerTab })));
const ChunkSimulatorTab = lazy(() => import('./components/chunking').then(m => ({ default: m.ChunkSimulatorTab })));
const PassageScorerTab = lazy(() => import('./components/chunking').then(m => ({ default: m.PassageScorerTab })));
const CriteriaTab = lazy(() => import('./components/criteria').then(m => ({ default: m.CriteriaTab })));
const QfoSimulatorTab = lazy(() => import('./components/qfo-simulator').then(m => ({ default: m.QfoSimulatorTab })));
const CloudflareTab = lazy(() => import('./components/cloudflare').then(m => ({ default: m.CloudflareTab })));
const TrafficOverview = lazy(() => import('./components/TrafficOverview'));
const AgentServeAnalytics = lazy(() => import('./components/AgentServeAnalytics'));
const KnowledgeTab = lazy(() => import('./components/KnowledgeTab'));
const LanguageModelTab = lazy(() => import('./components/LanguageModelTab'));
const LinkAnalysisTab = lazy(() => import('./components/LinkAnalysisTab'));
const FlowTab = lazy(() => import('./components/FlowTab'));
const FlowIssuesSubtab = lazy(() => import('./components/FlowIssuesSubtab'));
const AiDetectorTab = lazy(() => import('./components/AiDetectorTab').then(m => ({ default: m.AiDetectorTab })));
const AiVocabularyTab = lazy(() => import('./components/AiVocabularyTab'));
const AiContextTab = lazy(() => import('./components/ai-context/AiContextTab'));
const AnswerEngineTab = lazy(() => import('./components/answer-engine').then(m => ({ default: m.AnswerEngineTab })));
const LinkOpportunitiesPage = lazy(() => import('./components/LinkOpportunitiesPage'));
const CrawlPageDetails = lazy(() => import('./components/CrawlPageDetails'));
const EntityProfileRoute = lazy(() => import('./components/EntityProfileRoute'));
const FlowTopicRoute = lazy(() => import('./components/FlowTopicRoute'));
const SiteSelectorRoute = lazy(() => import('./components/SiteSelectorRoute'));
const QuestionExplorerTab = lazy(() => import('./components/QuestionExplorerTab'));
const ArenaTab = lazy(() => import('./components/ArenaTab'));
const CompareTab = lazy(() => import('./components/CompareTab'));

/**
 * Bridges route-rendered tabs to the existing prop-based component API.
 * Reads siteId from SiteContext and passes it as websiteId.
 * isActive is always true since only the matched route renders.
 */
const TabRoute = ({ component: Component, containerClass, wrapperClass, extraProps }) => {
  const { siteId, site } = useSite();
  const content = (
    <Component websiteId={siteId} isActive={true} {...(extraProps?.({ site }) || {})} />
  );

  return (
    <div className={`flex-1 flex flex-col min-h-0 ${containerClass || ''}`}>
      {wrapperClass ? <div className={`flex-1 ${wrapperClass}`}>{content}</div> : content}
    </div>
  );
};

export const siteSelectorRoute = (
  <Route index element={<SiteSelectorRoute />} />
);

export const verifyChildRoutes = (
  <>
    <Route index element={<Navigate to="crawl" replace />} />

    {/* Sidebar tabs */}
    <Route path="chat" element={<TabRoute component={ChatTab} extraProps={(p) => ({ siteUrl: p.site?.displayUrl })} />} />
    <Route path="verify-tab" element={<TabRoute component={VerifyTab} />} />
    <Route path="search" element={<TabRoute component={SearchTab} />} />
    <Route path="claims" element={<TabRoute component={ClaimsTab} />} />
    <Route path="crawl" element={<TabRoute component={CrawlTab} />} />
    <Route path="crawl/:pageId" element={
      <div className="flex-1 flex flex-col min-h-0">
        <CrawlPageDetails />
      </div>
    } />
    <Route path="answer-spy" element={<TabRoute component={CriteriaTab} containerClass="overflow-y-auto p-4" extraProps={(p) => ({ site: p.site })} />} />
    <Route path="answer-spy/:analysisId" element={<TabRoute component={CriteriaTab} containerClass="overflow-y-auto p-4" extraProps={(p) => ({ site: p.site })} />} />
    <Route path="optimizer" element={<TabRoute component={CitationOptimizerTab} containerClass="overflow-y-auto p-4" />} />
    <Route path="discovery" element={<TabRoute component={DiscoveryTab} />} />
    <Route path="content-lab" element={<TabRoute component={ChunkAnalyzerTab} />} />
    <Route path="qfo" element={<TabRoute component={QfoSimulatorTab} containerClass="overflow-y-auto" />} />
    <Route path="gsc" element={<TabRoute component={GscView} />} />
    <Route path="search-console" element={<SearchConsolePage />} />
    <Route path="search-console/report" element={<GscReport />} />
    <Route path="organic-traffic" element={<TabRoute component={TrafficOverview} containerClass="overflow-y-auto" />} />
    <Route path="crawlers" element={<TabRoute component={CloudflareTab} containerClass="overflow-y-auto p-4" extraProps={(p) => ({ siteDomain: p.site?.displayUrl })} />} />
    <Route path="agent-serve" element={<TabRoute component={AgentServeAnalytics} containerClass="overflow-y-auto" />} />
    <Route path="intelligence" element={<TabRoute component={KnowledgeTab} />} />
    <Route path="intelligence/entity/:profileId" element={<EntityProfileRoute />} />
    <Route path="language-model" element={<TabRoute component={LanguageModelTab} />} />
    <Route path="link-analysis" element={<TabRoute component={LinkAnalysisTab} />} />
    <Route path="flow" element={<TabRoute component={FlowTab} />} />
    <Route path="flow/topic/:topicId" element={<FlowTopicRoute />} />
    <Route path="issues" element={<TabRoute component={FlowIssuesSubtab} containerClass="overflow-y-auto px-3 md:px-6 pt-2 pb-6" />} />
    <Route path="link-opportunities" element={<TabRoute component={LinkOpportunitiesPage} containerClass="overflow-y-auto" />} />
    <Route path="ai-detector" element={<TabRoute component={AiDetectorTab} containerClass="overflow-y-auto" />} />
    <Route path="ai-vocabulary" element={<TabRoute component={AiVocabularyTab} containerClass="overflow-y-auto" />} />
    <Route path="ai-context" element={<TabRoute component={AiContextTab} />} />
    <Route path="answer-engine" element={<TabRoute component={AnswerEngineTab} containerClass="overflow-y-auto" />} />
    <Route path="compare/*" element={<TabRoute component={CompareTab} />} />

    {/* Non-sidebar tabs (reachable via in-app links) */}
    <Route path="links" element={<TabRoute component={LinksView} />} />
    <Route path="index" element={<TabRoute component={IndexView} wrapperClass="overflow-y-auto p-6" />} />
    <Route path="citations" element={<TabRoute component={CitationAnalysisTab} />} />
    <Route path="diagnostic" element={<TabRoute component={PageDiagnosticTab} containerClass="overflow-y-auto p-4" />} />
    <Route path="chunking" element={<TabRoute component={ChunkSimulatorTab} />} />
    <Route path="passage-scorer" element={<TabRoute component={PassageScorerTab} />} />
  </>
);
